import { CircularProgress } from '@mui/material'
import {
	GET_MY_ORGANIZATION,
	getMyOrganization,
	postMyOrganizationBanner,
	postMyOrganizationProfileImage,
	postMyOrganizationTab
} from 'api'
import {
	Button,
	DynamicTabContent,
	handleValid,
	Helper,
	initialStateModal,
	Layout,
	ModalConfirm,
	Slide,
	SlideBanner,
	sortByOrder,
	sortComponents,
	sortMenu,
	Title,
	Typography
} from 'components'
import { CardOpstions } from 'components/cardOptions'
import { arrayOptionsCardType } from 'components/cardOptions/cardOptions.type'
import { SliderColors } from 'components/myOrganization/slideColors'
import { SlideProfileImages } from 'components/myOrganization/slideProfileImages'
import { MenuContext } from 'context'
import { useToast } from 'hooks'
import {
	CompoenentCreateType,
	Icons,
	Images,
	MyOrganizationResponse,
	ProfileImageMyOrganizationItem,
	TabMyOrganizationItem,
	TypographyTypes
} from 'interfaces'
import React, { useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { useQuery } from 'react-query'
import Cookies from 'universal-cookie'

export interface StateButtonRdio {
	value: string
	index: number
}

export const MyOrganization = (): JSX.Element => {
	const cookies = new Cookies()
	const userId = cookies.get('userId')
	const organizationId = cookies.get('organization_id')
	const { setOrganizationLogo } = React.useContext(MenuContext)
	const { showToast } = useToast()
	const [tabActive, setTabActive] = useState(0)
	const [data, setData] = useState<MyOrganizationResponse>()
	const [modalProps, setModalProps] = useState(initialStateModal)
	const [openSlideProfile, setOpenSlideProfile] = useState(false)
	const [openSlideBanner, setOpenSlideBanner] = useState(false)
	const [openSlideColors, setOpenSlideColors] = useState(false)
	const [activeResource, setActiveResource] = useState<boolean>(false)
	const [defaultComponent] = useState<CompoenentCreateType>()
	const [loading, setLoading] = useState(false)
	const [errorForm, setErrorForm] = useState(false)
	const [fieldError, setFieldError] = useState<string[]>([])

	const nameSection = activeResource ? 'Card' : 'Tab'

	const {
		control,
		setValue,
		watch,
		reset,
		register,
		formState: { errors, isValid }
	} = useForm({
		mode: 'onChange',
		defaultValues: {
			tabs: data?.cards ?? []
		}
	})
	const { fields, append, remove } = useFieldArray({ control, name: 'tabs' })

	const { isLoading, refetch, isFetching } = useQuery({
		queryKey: [GET_MY_ORGANIZATION, userId, organizationId],
		queryFn: () => getMyOrganization(organizationId),
		onSuccess: response => {
			if (response) {
				setActiveResource(response?.active_resource ?? false)
				setData(response as MyOrganizationResponse)
				const sortedTabs = response.cards.map((tab: TabMyOrganizationItem) => ({
					...tab,
					components: sortComponents(tab.components),
					setup_menu: tab.setup_menu
						? sortByOrder(tab.setup_menu.map(sortMenu))
						: []
				}))

				setValue('tabs', sortedTabs)
				if (response.cards.length === 0) {
					setTabActive(-1)
				}
			}
		},
		refetchOnWindowFocus: false,
		retry: 2
	})

	const messageToast = (value: { message: string } | null): void => {
		if (value) {
			showToast('Success', value?.message, 'success')
			refetch()
		}
	}

	const arrayOptionsCard: arrayOptionsCardType[] = [
		{
			title: 'Banners',
			descripcion: 'Create impact',
			action: setOpenSlideBanner,
			image: Images.emtyBanner
		},
		{
			title: 'Colors',
			descripcion: 'Customize your space',
			action: setOpenSlideColors,
			image: Images.emtyColor
		},
		{
			title: 'Profile Images',
			descripcion: 'Display your brand',
			action: setOpenSlideProfile,
			image: Images.resorceOrg
		}
	]

	const onSaveProfile = async (
		aux: ProfileImageMyOrganizationItem
	): Promise<void> => {
		setLoading(true)
		const responseProfile = await postMyOrganizationProfileImage(
			organizationId,
			aux
		)

		// Update cookie and context with new logo URL
		if (responseProfile && aux.icon) {
			// Remove any existing query parameters before adding timestamp
			const baseUrl = aux.icon.split('?')[0]
			const logoUrlWithTimestamp = `${baseUrl}?timestamp=${Date.now()}`
			cookies.set('organization_logo', logoUrlWithTimestamp, {
				path: '/'
			})
			// Update context to trigger re-render of logo in layout
			setOrganizationLogo(logoUrlWithTimestamp)
			messageToast(responseProfile)
			setLoading(false)
			setOpenSlideProfile(false)
		} else {
			setLoading(false)
			messageToast(responseProfile)
		}
	}

	const saveData = async (): Promise<void> => {
		const formData = watch()
		setLoading(true)
		const responseProfile = await postMyOrganizationTab(
			organizationId,
			formData.tabs as unknown as TabMyOrganizationItem[]
		)
		setLoading(false)
		if (responseProfile) {
			messageToast(responseProfile)
		} else {
			showToast(
				'Changes could not be saved',
				'Please review your content and try again',
				'error',
				{
					duration: 5000
				}
			)
		}
	}

	const confirmDeleteImage = (index: number): void => {
		const updatedTabs = (watch().tabs as TabMyOrganizationItem[]).filter(
			(_, i) => i !== index
		)

		setModalProps({
			open: true,
			title: `Delete ${nameSection}`,
			description: `Are you sure you want to delete this ${nameSection.toLowerCase()}? This action cannot be undone.`,
			onConfirm: async () => {
				remove(index)

				const nameType = `tabs.${index}`
				const updatedErrors = fieldError
					.filter(error => !error.includes(nameType))
					.map(error =>
						error.replace(/tabs\.(\d+)/, (_, num) => {
							const newIndex =
								parseInt(num, 10) > index ? parseInt(num, 10) - 1 : num
							return `tabs.${newIndex}`
						})
					)

				setFieldError(updatedErrors)
				handleValid(nameType, true, setFieldError)
				setModalProps(initialStateModal)

				try {
					setLoading(true)
					const res = await postMyOrganizationTab(organizationId, updatedTabs)
					messageToast(res)
				} catch (error) {
					console.error('Error deleting card:', error)
				} finally {
					setLoading(false)
				}
			}
		})
	}

	const messageHelpers = activeResource
		? 'Create unlimited cards. Each Tab can have multiple Components, Redirects to external sites or Components so you can create your own customized pages'
		: 'Add as many Components as you like to display beneath each Tab. Select +New Component to see all options.'

	return (
		<Layout>
			{loading || isLoading || isFetching ? (
				<div className="flex items-center justify-center w-full h-screen py-2 px-1 ">
					<CircularProgress disableShrink />
				</div>
			) : (
				<>
					<div className="md:w-4/12 p-2 ">
						<Title
							icon={Icons.myOrganization}
							title="My Organization"
							subtitle="Set up your resources"
						/>
					</div>

					<div className="flex flex-col md:flex-row">
						{arrayOptionsCard.map((element, index) => (
							<CardOpstions data={element} key={`${element}_${index}`} />
						))}
					</div>

					<div className="w-full py-3 flex justify-between items-center">
						<Typography
							title={`Manage ${nameSection}s`}
							fontLeading="22/27"
							type={TypographyTypes.h1}
							className="text-gray-16"
						/>
						<Helper text={messageHelpers} className="relative" />
					</div>

					{fields.map((component, index) => (
						<Controller
							control={control}
							key={`tabs-${component.id}`}
							name={`tabs.${index}`}
							render={({ field }) => {
								return (
									<DynamicTabContent
										active={index}
										data={field.value}
										deletedTab={confirmDeleteImage}
										saveData={saveData}
										control={control}
										reset={reset}
										setValue={setValue}
										register={register}
										watch={watch}
										errors={errors}
										isValid={isValid}
										errorForm={errorForm}
										setErrorForm={setErrorForm}
										fieldError={fieldError}
										setFieldError={setFieldError}
										nameSection={nameSection}
										resourceUpdated={activeResource}
									/>
								)
							}}
						/>
					))}

					{(activeResource || fields.length < 4) && (
						<div
							aria-hidden="true"
							className="min-w-[100px] justify-center items-center"
						>
							<Button
								label={`Add ${nameSection}`}
								className="w-full md:w-auto my-2"
								iconRight={Icons.plus}
								color="white"
								onClick={() => {
									const maxOrder = Math.max(
										...fields.map(c => c.section_order as number),
										0
									)
									append({
										organization_id: organizationId,
										name: `New ${nameSection}`,
										section_order: maxOrder + 1,
										components: [],
										audience: {
											tags: []
										},
										setup_card: {
											link: {
												enabled_link: false,
												url: ''
											},
											menu: {
												enabled_menu: false
											}
										},
										setup_menu: [],
										image_card: ''
									})
								}}
							/>
						</div>
					)}

					{modalProps.open && (
						<ModalConfirm
							open={modalProps.open}
							setOpen={() => setModalProps(initialStateModal)}
							hasCancel
							buttonLabelCancel="Cancel"
							buttonLabel="Yes"
							flexButton="row"
							handleCancel={() => setModalProps(initialStateModal)}
							onClickConfirm={modalProps.onConfirm}
							icon={Icons.warningActive}
							title={modalProps.title}
							descripcion={modalProps.description}
							classNameButton="h-[32px] w-[151px] m-0 !py-0"
						/>
					)}
					<Slide
						open={openSlideProfile}
						setOpen={setOpenSlideProfile}
						title="Profile Images"
					>
						<SlideProfileImages
							key={`profile-images-${organizationId}`}
							active={tabActive}
							dataForm={defaultComponent}
							setOpen={setOpenSlideProfile}
							data={data}
							setData={setData}
							setLoading={setLoading}
							onSave={onSaveProfile}
							resourceUpdated={activeResource}
						/>
					</Slide>
					<Slide
						open={openSlideBanner}
						setOpen={setOpenSlideBanner}
						title="Banner"
					>
						<SlideBanner
							active={tabActive}
							dataForm={defaultComponent}
							setOpen={setOpenSlideBanner}
							data={data}
							setData={setData}
							setLoading={setLoading}
							onSave={async aux => {
								const response = await postMyOrganizationBanner(
									organizationId,
									aux
								)
								messageToast(response)
							}}
						/>
					</Slide>

					<Slide
						open={openSlideColors}
						setOpen={setOpenSlideColors}
						title="Color"
					>
						<SliderColors
							data={data}
							slideOpen={openSlideColors}
							setOpen={setOpenSlideColors}
							setData={setData}
							onSave={onSaveProfile}
						/>
					</Slide>
				</>
			)}
		</Layout>
	)
}
